import React, { useRef, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';

import { ResponseError } from '../../../common/spdCore/autogenerated/spdApiClient';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';

interface CsvImportSectionProps {
  title: string;
  importFunction: (
    file: File,
  ) => Promise<{ imported?: number; skipped?: number }>;
  onImported: () => void;
  buttonText?: string;
}

export const CsvImportSection: React.FC<CsvImportSectionProps> = ({
  title,
  importFunction,
  onImported,
  buttonText = 'Importuj CSV',
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    imported: number;
    skipped: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);

  const handleHide = () => {
    setError(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setShowModal(false);
  };

  const handleSubmit = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      setError('Wybierz plik CSV.');
      return;
    }
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const res = await importFunction(file);
      setResult({ imported: res.imported ?? 0, skipped: res.skipped ?? 0 });
      onImported();
    } catch (e: unknown) {
      if (e instanceof ResponseError) {
        const errors = await responseErrors(e);
        setError(errors[0] ?? 'Błąd importu.');
      } else if (e instanceof Error) {
        setError(e.message || 'Błąd importu.');
      } else {
        setError('Błąd importu.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button variant="outline-secondary" onClick={() => setShowModal(true)}>
        {buttonText}
      </Button>
      <Modal show={showModal} onHide={handleHide}>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {result !== null && (
            <Alert variant="success">
              Zaimportowano {result.imported}{' '}
              {result.imported === 1 ? 'wpłatę' : 'wpłaty'}.
              {result.skipped > 0 &&
                ` Pominięto ${result.skipped} (duplikaty).`}
            </Alert>
          )}
          <Form.Group>
            <Form.Label>Plik CSV</Form.Label>
            <Form.Control type="file" accept=".csv" ref={fileInputRef} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Importuję...' : 'Importuj'}
          </Button>
          <Button variant="secondary" onClick={handleHide} disabled={loading}>
            Zamknij
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
